import { serverSidePagePrepare } from "@lib/hooks"
import { observer } from "mobx-react"
import {
  DocumentTemplate,
  Header,
  Page,
  PureLogo,
  useStore,
} from "@features/common"
import { RouterLink } from "@lib/routing"
import { scrollToTop } from "@lib/scroll"
import { IconHamburger } from "@lib/icons"
import { PrivacyLogo } from "@features/privacy"
import {
  DesktopTableOfContents,
  LegalSectionView,
  PreviousVersions,
  TouchTableOfContents,
} from "@features/terms"
import { dateFormater } from "@lib/date"
import { redirect } from "@lib/isomorphic"
import { useEffect, useState } from "react"
import { useRouter } from "next/router"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    const { effective_date } = ctx.query
    const queryDate = Array.isArray(effective_date)
      ? effective_date[0]
      : effective_date

    await Models.FaqModel.init()
    try {
      await Models.LegalModel.getConsumerHealthPageData(queryDate)
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const ConsumerHealthPage = observer(function CaliforniaPrivacyPage() {
  const {
    GlobalModel: { openMenu },
    LegalModel: {
      consumerHealthSections,
      consumerHealthTitle,
      consumerHealthEffectiveDate,
      consumerHealthVersions,
    },
  } = useStore()
  const { asPath } = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  return (
    <Page
      meta={{
        title: "Consumer Health Data Privacy Policy",
      }}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/legal/consumer-health-data-privacy-policy"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
            >
              <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                <PrivacyLogo />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      seoH1={consumerHealthTitle}
    >
      <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
        {consumerHealthSections.length > 1 ? (
          <TouchTableOfContents tableOfContents={consumerHealthSections} />
        ) : null}
        <DocumentTemplate
          sidebar={
            consumerHealthSections.length > 1 ? (
              <DesktopTableOfContents
                tableOfContents={consumerHealthSections}
              />
            ) : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {consumerHealthSections.map((i) => (
              <LegalSectionView data={i} key={i.id} id={i.id} />
            ))}
            <div>
              <div className="pb-3">
                <strong>Effective date:</strong>{" "}
                {consumerHealthEffectiveDate
                  ? dateFormater(
                      new Date(consumerHealthEffectiveDate),
                      "dd MMMM yyyy",
                      "en",
                    )
                  : ""}
              </div>
              <PreviousVersions versions={consumerHealthVersions} />
            </div>
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default ConsumerHealthPage
